import {useEffect, useState} from 'react';
import { useTheme } from '@mui/material/styles';
import debounce from 'lodash/debounce';
import { useErrorBoundary } from 'react-error-boundary';

import Main from '../../layouts/Main';
import Container from '../../components/Container';
import { Divider, Grid2 as Grid, Typography, useMediaQuery, Card, List, ListItem, ListItemIcon, Avatar, Box, Link, FormControl, Autocomplete, Stack, TextField, InputAdornment, CircularProgress} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BackupTableIcon from '@mui/icons-material/BackupTable';

import TownElectedOfficials from './components/TownElectedOfficials';
import ElectedFiler from '../../types/filers/electedFiler';
import TownInfo from '../../types/municipal/townInfo';
import TownFilersReports from './components/TownFilersReports';
import YtdReport from '../../types/reports/ytdReport';
import PageBreadCrumbs from '../../components/PageBreadcrumbs';

interface TownDropdownOption {
  code: number;
  name: string;
}

const Town = (): JSX.Element => {

  document.title = 'OCPF - Municipal Town Info';

  //pass search from browser
  const queryParams = new URLSearchParams(window.location.search)

    const theme = useTheme();
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));
    const { showBoundary } = useErrorBoundary();  
    const [electedFilers, setElectedFilers] = useState<ElectedFiler[]>([]);
    const [filersReports, setFilersReports] = useState<YtdReport[]>([]);
    const [cityCode, setCityCode] = useState<number>(Number(queryParams.get('cityCode')) || 35);
    const [cityName, setCityName] = useState("Boston");
    const [townInfo, setTownInfo] = useState<TownInfo>();

    const [townListOpen, setTownListOpen] = useState(false);
    const [townOptions, setTownOptions] = useState<readonly TownDropdownOption[]>([]);
    const [selectedTown, setSelectedTown] = useState<TownDropdownOption | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);


    useEffect(() => {
        getTownInfo();
    }, [cityCode])

    const handleTownListOpen = () => {
      setTownListOpen(true);
    };
  
    const handleTownListClose = () => {
      setTownListOpen(false);
      setTownOptions([]);
    };

    const refreshTownList = async (searchInput: string) => {
      try{
        if(searchInput){
          let encodedSearchInput = encodeURIComponent(searchInput);
          let url = `${process.env.REACT_APP_API_BASE_URL}/municipalityList?searchPhrase=${encodedSearchInput}`;
          await fetch(url)
          .then(result => result.json())
          .then(result => {
              setTownOptions(result);
          });
        }
        else{
          setTownOptions([]);
        }
  
      }catch(error){
        console.error('Uncaught Error:', error);
      }
    }

    const updateSelectedTown = (town: TownDropdownOption) => {
      let cityCode = town.code;
      setCityCode(cityCode);
      setSelectedTown(town);

      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);
      params.set('cityCode', cityCode.toString());
      
      // Update the URL in the browser's history without reloading
      window.history.pushState({}, '', `${url.pathname}?${params.toString()}`);
    }
  
    const clearSelectedTown = () => {
      setSelectedTown(undefined);
    }
  

    const getTownInfo = async () => {
      setIsLoading(true);
    try{
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/municipality/byCode/${cityCode}/2024`)
      .then(result => {
        if (!result.ok) {
          showBoundary(new Error('Failed to get the town data.'));
        }
        return result.json();
      })
      .then(result => {
        setIsLoading(false);
        setTownInfo(result);
        setElectedFilers(result.electedFilers); 
        setFilersReports(result.filersReports);
      }
      );
    }catch(error){
        console.error('Uncaught Error:', error);
    }
  }

  const filterByName = debounce((event: any) => {
    let searchInput = event.target.value.toLowerCase();
    (async () => {
      refreshTownList(searchInput);
    })();
  }, 800);

  return (
    <Main>
      <Container>
      <PageBreadCrumbs></PageBreadCrumbs>
        <Grid container>
          <Grid size={{ xs: 12, md: 7 }}>
            <Typography
              variant="h5"
              align={'left'}
              data-aos={'fade-up'}
              sx={{
                fontWeight: 700,
              }}
              paddingTop={3}
              gutterBottom
              marginBottom={2}
            >
              {townInfo?.city}
              <Typography component={"span"} variant="subtitle2" color={'text.secondary'} marginLeft={1}>
                {townInfo?.county}
              </Typography>     
          </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 5}}>
            <Stack direction="row" spacing={2} alignItems={"center"}>
              <FormControl variant='standard'>
                  <Autocomplete
                    filterOptions={(x) => x}
                    sx={{ width: isSM ?200: 280 }}
                    onChange={(event: any, newValue: TownDropdownOption | null) => {
                      if (newValue) {
                          updateSelectedTown(newValue);
                      }
                      else{
                          clearSelectedTown();
                      }
                    }}
                    open={townListOpen}
                    onOpen={handleTownListOpen}
                    onClose={handleTownListClose}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => 
                      (
                        <Stack 
                          direction="row" 
                          display={"flex"} 
                          justifyContent={"flex-start"}
                          key={option.code}
                          marginLeft={1}
                        >
                            <AccountBalanceIcon />
                            <Typography variant='body2' {...props}>
                                {option.name}
                            </Typography>
                        </Stack>
                        )
                    }
                    options={townOptions}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='standard'
                        aria-label='Search town or city'
                        title='Search town or city'
                        placeholder={isSM?"Search...":"Search another city or town..."}
                        onChange={filterByName}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon color="primary" />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />
              </FormControl>
              <Typography variant='subtitle1' component={Link} href={'/Municipal/Map'} underline='none'><i>Back to Map</i></Typography>
            </Stack>  
          </Grid>
        </Grid>
        <Divider/>
        {/*SUMMARY*/}
          <Grid container spacing={{ xs: 2, md: 4 }} margin={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ p: 0, width:1, height:1 }}>
                <List sx={{ p: 0, m: 0 }}>
                  <ListItem alignItems={'flex-start'}>
                    <ListItemIcon>
                      <Avatar
                        variant={'rounded'}
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: theme.palette.primary.main,
                          color: theme.palette.common.white,
                        }}
                      >
                        <PeopleOutlineIcon />
                      </Avatar>
                    </ListItemIcon>
                    <Box marginY={0.5}>
                      <Typography color={'text.secondary'}>
                        Total Population (2020)
                      </Typography>
                      <Typography
                        variant={'subtitle1'}
                        color={'text.primary'}
                        fontWeight={700}
                        data-aos={'fade-up'}
                      >
                        {townInfo?.pop2020.toLocaleString()}
                      </Typography>
                    </Box>
                  </ListItem>
                </List>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ p: 0, width:1, height:1 }}>
                <List sx={{ p: 0, m: 0 }}>
                  <ListItem alignItems={'flex-start'}>
                    <ListItemIcon>
                      <Avatar
                        variant={'rounded'}
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: theme.palette.primary.main,
                          color: theme.palette.common.white,
                        }}
                      >
                        <AccountBalanceIcon />
                      </Avatar>
                    </ListItemIcon>
                    <Box marginY={0.5}>
                      <Typography color={'text.secondary'}>
                        Recent Legislative Races
                      </Typography>
                      <Typography
                        component={Link}
                        href={'#election-races'}
                        underline={'hover'}
                        variant={'subtitle2'}
                        data-aos={'fade-up'}
                      >
                        <i>Learn More</i>
                      </Typography>
                    </Box>
                  </ListItem>
                </List>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ p: 0, width:1, height:1 }}>
                <List sx={{ p: 0, m: 0 }}>
                  <ListItem alignItems={'flex-start'}>
                    <ListItemIcon>
                      <Avatar
                        variant={'rounded'}
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: theme.palette.primary.main,
                          color: theme.palette.common.white,
                        }}
                      >
                        <BackupTableIcon />
                      </Avatar>
                    </ListItemIcon>
                    <Box marginY={0.5}>
                      <Typography color={'text.secondary'}>
                        Municipal Officers Reports
                      </Typography>
                      {
                        townInfo?.localReportsUrl &&
                        <Typography
                            component={Link}
                            href={townInfo?.localReportsUrl}
                            target='_blank'
                            underline={'hover'}
                            variant={'subtitle2'}
                            data-aos={'fade-up'}
                          >
                          <i>Click to visit town website</i>
                          </Typography>
                      }
                      {
                        townInfo && townInfo.localReportsUrl.length==0 &&
                        <Typography
                          component={"i"}
                          color="warning"
                          variant={'subtitle2'}
                          data-aos={'fade-up'}
                        >
                        Please contact the local clerk for locally filed reports.
                        </Typography>
                      }
                    </Box>
                  </ListItem>
                </List>
              </Card>
            </Grid>
          </Grid>
        <Divider/>
        <TownElectedOfficials officers={electedFilers}></TownElectedOfficials>
        <Divider id="election-races"/>
        <TownFilersReports reports={filersReports} cityCode={cityCode}></TownFilersReports>
         {/*loading indicator*/}
                <CircularProgress
                    aria-label='loding circle'
                    title='loading circle'
                    size={50}
                    className={isLoading? "": "hidden"}
                    sx={{
                      position: "fixed",
                      left: "50%",
                      top: "20%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 2
                     }}
                />
      </Container>
    </Main>
  );
};

export default Town;